/**
 * One invite request.
 *
 * This is where a decision is taken, so it shows everything the decision needs:
 * the applicant, their referrer, their earlier requests in this product, and the
 * invite code if one was already issued.
 *
 * The rejection reason is the text the applicant will read. The preset list is
 * rendered into that text area rather than stored as an enum, so the wording
 * follows the console language and an administrator can still write their own.
 */

import { useState } from 'react'
import { Link, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import { REJECT_REASON_PRESETS } from '@shared/constants'

import { ErrorAlert, ErrorBlock, LoadingBlock } from '../../components/layout/states'
import { Alert, AlertDescription } from '../../components/ui/alert'
import { Button } from '../../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import { Chip, ChipGroup } from '../../components/ui/chip'
import { Textarea } from '../../components/ui/input'
import {
  useAdminApplication,
  useApproveApplication,
  useRejectApplication,
  useResendCode,
} from '../../hooks/use-admin'
import { formatUnixSeconds, joinList } from '../../lib/format'
import { currentLocale } from '../../lib/i18n'
import { AdminPage, DetailRow, StateBadge, adminErrorText, applicationVariant, codeStateVariant, mailVariant } from './shared'

export function AdminApplicationDetailPage() {
  const { t } = useTranslation()
  const { id = '' } = useParams()
  const locale = currentLocale()

  const detail = useAdminApplication(id)
  const approve = useApproveApplication()
  const reject = useRejectApplication()
  const resend = useResendCode()

  const [reason, setReason] = useState('')
  const [preset, setPreset] = useState<string | null>(null)

  // Read as text rather than as an `ApiError`: a request that never reached the
  // server still has to say something instead of leaving the page silent.
  const errorText = adminErrorText(approve.error ?? reject.error ?? resend.error, t)

  function choosePreset(key: string) {
    setPreset(key)
    setReason(t(`admin.rejectReason.${key}`))
  }

  const application = detail.data?.application
  const applicant = detail.data?.applicant
  const code = detail.data?.code
  const pending = application?.status === 'pending'

  return (
    <AdminPage
      title={t('admin.application.title')}
      description={application?.email}
      current={t('admin.nav.applications')}
    >
      {detail.isPending ? <LoadingBlock label={t('common.loading')} /> : null}
      {detail.isError ? (
        <ErrorBlock
          title={t('admin.common.error')}
          retryLabel={t('admin.common.retry')}
          onRetry={() => void detail.refetch()}
        />
      ) : null}

      {errorText ? (
        <ErrorAlert message={errorText} />
      ) : null}

      {approve.isSuccess && approve.data ? (
        <Alert variant={approve.data.delivered ? 'success' : 'destructive'}>
          <AlertDescription>
            {approve.data.delivered
              ? t('admin.application.approvedDelivered')
              : t('admin.application.approvedNotDelivered')}
          </AlertDescription>
        </Alert>
      ) : null}

      {application && applicant ? (
        <>
          {/*
            The decision comes first for a pending request: it is the only thing
            this page exists to do, and in a short viewport the reference material
            below it pushed the buttons past the fold.
          */}
          {pending ? (
            <Card>
              <CardHeader>
                <CardTitle>{t('admin.application.decide')}</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                <Button
                  className="self-start"
                  disabled={approve.isPending}
                  onClick={() => approve.mutate(application.id)}
                >
                  {t('admin.application.approve')}
                </Button>

                <div className="flex flex-col gap-2">
                  <ChipGroup label={t('admin.application.rejectPresetLabel')}>
                    {REJECT_REASON_PRESETS.map((key) => (
                      <Chip
                        key={key}
                        selected={preset === key}
                        onClick={() => choosePreset(key)}
                        type="button"
                      >
                        {t(`admin.rejectReason.${key}`)}
                      </Chip>
                    ))}
                  </ChipGroup>
                  <Textarea
                    value={reason}
                    rows={3}
                    maxLength={300}
                    aria-label={t('admin.application.rejectReason')}
                    placeholder={t('admin.application.rejectReasonPlaceholder')}
                    onChange={(event) => {
                      setReason(event.target.value)
                      setPreset(null)
                    }}
                  />
                  <Button
                    variant="destructive"
                    className="self-start"
                    disabled={reject.isPending || reason.trim().length === 0}
                    onClick={() => reject.mutate({ id: application.id, reason: reason.trim() })}
                  >
                    {t('admin.application.reject')}
                  </Button>
                  {reason.trim().length === 0 ? (
                    <p className="text-muted-foreground text-xs">
                      {t('admin.application.rejectNeedsReason')}
                    </p>
                  ) : null}
                </div>
              </CardContent>
            </Card>
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle>{t('admin.application.applicant')}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col">
              <DetailRow label={t('admin.users.email')}>{application.email}</DetailRow>
              <DetailRow label={t('admin.users.nickname')}>
                {application.nickname ?? t('admin.users.noNickname')}
              </DetailRow>
              <DetailRow label={t('admin.users.registeredAt')}>
                {formatUnixSeconds(applicant.createdAt, locale)}
              </DetailRow>
              <DetailRow label={t('admin.users.loginMethods')}>
                {joinList(
                  applicant.loginMethods.map((method) => t(`admin.users.loginMethod.${method}`)),
                  t,
                )}
              </DetailRow>
              <DetailRow label={t('admin.users.activated')}>
                {applicant.isInvited ? t('common.yes') : t('common.no')}
              </DetailRow>
              <DetailRow label={t('admin.users.referrer')}>
                {applicant.referrerEmail ?? t('admin.users.noReferrer')}
                {applicant.referrerCode ? ` (${applicant.referrerCode})` : ''}
                {applicant.referralChannel === 'direct'
                  ? ` ${t('admin.application.defaultReferrer')}`
                  : ''}
              </DetailRow>
              <DetailRow label={t('admin.users.referralChannel')}>
                {applicant.referralChannel
                  ? t(`admin.referralChannel.${applicant.referralChannel}`)
                  : '—'}
              </DetailRow>
              {applicant.bannedUntil ? (
                <DetailRow label={t('admin.users.bannedUntil')}>
                  {formatUnixSeconds(applicant.bannedUntil, locale)}
                </DetailRow>
              ) : null}
              <DetailRow label={t('admin.application.status')}>
                <StateBadge
                  label={t(`admin.applicationStatus.${application.status}`)}
                  variant={applicationVariant(application.status)}
                />
              </DetailRow>
              <DetailRow label={t('admin.application.submittedAt')}>
                {formatUnixSeconds(application.createdAt, locale)}
              </DetailRow>
              {application.decisionNote ? (
                <DetailRow label={t('admin.application.decisionNote')}>
                  {application.decisionNote}
                </DetailRow>
              ) : null}
              {application.reason ? (
                <DetailRow label={t('admin.application.reason')}>{application.reason}</DetailRow>
              ) : null}
            </CardContent>
          </Card>

          {code ? (
            <Card>
              <CardHeader>
                <CardTitle>{t('admin.application.code')}</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-3">
                <DetailRow label={t('admin.codes.suffix')}>••••{code.codeSuffix}</DetailRow>
                <DetailRow label={t('admin.codes.state')}>
                  <StateBadge
                    label={t(`admin.codeState.${code.state}`)}
                    variant={codeStateVariant(code.state)}
                  />
                </DetailRow>
                <DetailRow label={t('admin.codes.mail')}>
                  <StateBadge
                    label={t(`admin.mailStatus.${code.mailStatus}`)}
                    variant={mailVariant(code.mailStatus)}
                  />
                </DetailRow>
                <DetailRow label={t('admin.codes.expiresAtLabel')}>
                  {formatUnixSeconds(code.expiresAt, locale)}
                </DetailRow>
                {code.state === 'unused' ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="self-start"
                    disabled={resend.isPending}
                    onClick={() => resend.mutate(code.id)}
                  >
                    {t('admin.codes.resend')}
                  </Button>
                ) : null}
              </CardContent>
            </Card>
          ) : null}


          <Card>
            <CardHeader>
              <CardTitle>{t('admin.application.history')}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
              {detail.data?.history.map((entry) => (
                <div key={entry.id} className="flex items-center justify-between gap-2 text-sm">
                  <span className="text-muted-foreground">
                    {formatUnixSeconds(entry.createdAt, locale)}
                  </span>
                  <StateBadge
                    label={t(`admin.applicationStatus.${entry.status}`)}
                    variant={applicationVariant(entry.status)}
                  />
                </div>
              ))}
            </CardContent>
          </Card>

          <div>
            <Button asChild variant="outline" size="sm">
              <Link to="/admin/applications">{t('admin.application.back')}</Link>
            </Button>
          </div>
        </>
      ) : null}
    </AdminPage>
  )
}
